import { useAtom, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import type { ComplimentaryTrialEndEntryPoint } from '../../../contracts/complimentary-trial-end-analytics.types';
import { complimentaryTrialEndDevOverrideAtom } from '../../../data/billing-dev/complimentary-trial-end-overrides';
import {
  COMPLIMENTARY_TRIAL_END_ACTIVE_USAGE_PERCENT,
  type ComplimentaryTrialEndCohort,
  getComplimentaryDaysLeft,
} from '../../../data/query/complimentary-trial-end.utils';
import {
  complimentaryTrialEndBannerDismissedAtom,
  complimentaryTrialEndModalEntryPointAtom,
} from './complimentary-trial-end-atoms';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const ACTIVE_USAGE_PERCENT = 72;
const INACTIVE_USAGE_PERCENT = 12;

export type ComplimentaryTrialEndDevControls = {
  /** `null` while the real allowance drives the surfaces. */
  forced: { daysLeft: number; cohort: ComplimentaryTrialEndCohort } | null;
  force: (next: { daysLeft: number; cohort: ComplimentaryTrialEndCohort }) => void;
  clear: () => void;
  openModal: (entryPoint: ComplimentaryTrialEndEntryPoint) => void;
  resetBannerDismissal: () => void;
};

/**
 * Dev-tools controls for the complimentary usage end countdown. Forcing a state
 * shows the sidebar countdown, banner and T-7 modal without a real allowance;
 * it does nothing in production.
 */
export function useComplimentaryTrialEndDevControls(): ComplimentaryTrialEndDevControls {
  const [override, setOverride] = useAtom(complimentaryTrialEndDevOverrideAtom);
  const setModalEntryPoint = useSetAtom(complimentaryTrialEndModalEntryPointAtom);
  const setBannerDismissed = useSetAtom(complimentaryTrialEndBannerDismissedAtom);

  const daysLeft =
    override == null
      ? null
      : getComplimentaryDaysLeft({ cycle_end: override.cycleEnd }, new Date());
  const forced =
    override == null || daysLeft == null
      ? null
      : {
          daysLeft,
          cohort:
            override.usagePercent >= COMPLIMENTARY_TRIAL_END_ACTIVE_USAGE_PERCENT
              ? ('active' as const)
              : ('inactive' as const),
        };

  const force = useCallback(
    (next: { daysLeft: number; cohort: ComplimentaryTrialEndCohort }) => {
      // A minute short of the whole day, so the rounded-up count reads `daysLeft`.
      const cycleEnd = new Date(Date.now() + next.daysLeft * MS_PER_DAY - 60_000);
      setOverride({
        cycleEnd: cycleEnd.toISOString(),
        usagePercent:
          next.cohort === 'active' ? ACTIVE_USAGE_PERCENT : INACTIVE_USAGE_PERCENT,
      });
    },
    [setOverride]
  );

  const clear = useCallback(() => setOverride(null), [setOverride]);
  const resetBannerDismissal = useCallback(
    () => setBannerDismissed({}),
    [setBannerDismissed]
  );

  return {
    forced,
    force,
    clear,
    openModal: setModalEntryPoint,
    resetBannerDismissal,
  };
}
